import type { SelectOption } from '@movingimage-evp/mi-ui-component-library';

import { useReactiveVar } from '@apollo/client';
import {
  ChevronLeftIcon,
  InputDescription,
  Select,
  classNames,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorPage } from 'src/components/error-page';
import { useUserPermissions } from 'src/manager/hooks/user-permissions';

import { BrandingPreview } from './branding-preview';
import { NewThemeModal } from './new-theme-modal';
import { ThemeListItem } from './theme-list-item';
import { themePreviewVar } from './theme-preview';
import { ThemeSettings } from './theme-settings';
import {
  GetLsproBrandingSettingsDocument as GET_LSPRO_BRANDING_SETTINGS,
  type GetLsproBrandingSettingsQuery,
  useGetLsproBrandingSettingsQuery,
  useSetDefaultThemeMutation,
} from '../../../generated/graphql-manager';
import { useCurrentUser } from '../../hooks/current-user';

import accessDeniedOperatorImage from '../../../assets/access-denied-operator.svg';

import styles from './branding.module.css';
import playerStyles from '../../../viewer/views/stream/stream.module.css';

export function BrandingPage() {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const { isAccessToBrandingPageAllowed } = useUserPermissions();
  const preview = useReactiveVar(themePreviewVar);

  const { data } = useGetLsproBrandingSettingsQuery({ variables: { lsproId } });
  const themes = data?.lsproSettings?.themes;
  const defaultTheme = themes?.find(({ isDefault }) => isDefault);

  const [setDefaultTheme] = useSetDefaultThemeMutation();

  const handleChangeDefaultTheme = (option: SelectOption) => {
    const themeId = option.key;
    const selected = themes?.find((theme) => theme.id === themeId);

    if (!selected) return;

    setDefaultTheme({
      variables: { input: { lsproId, themeId } },
      optimisticResponse: {
        setDefaultTheme: {
          __typename: 'SetDefaultThemeSuccess',
          theme: {
            __typename: 'AppearanceTheme',
            id: selected.id,
            name: selected.name,
            mode: selected.mode,
            isDefault: true,
            assets: selected.assets,
            appearanceColors: selected.appearanceColors,
            fonts: selected.fonts,
          },
        },
      },
      update: (cache, { data }) => {
        if (data?.setDefaultTheme.__typename !== 'SetDefaultThemeSuccess') return;

        const cached = cache.readQuery<GetLsproBrandingSettingsQuery>({
          query: GET_LSPRO_BRANDING_SETTINGS,
          variables: { lsproId },
        });

        if (!cached?.lsproSettings?.themes) return;

        cache.writeQuery({
          query: GET_LSPRO_BRANDING_SETTINGS,
          variables: { lsproId },
          data: {
            ...cached,
            lsproSettings: {
              ...cached.lsproSettings,
              themes: cached.lsproSettings.themes.map((theme) => ({
                ...theme,
                isDefault: theme.id === themeId,
              })),
            },
          },
        });
      },
      onCompleted: (data) => {
        if (data.setDefaultTheme.__typename === 'SetDefaultThemeSuccess') {
          toast(t('manager.branding.notifications.defaultThemeUpdated', { themeName: selected.name }), 'positive');
        } else {
          notification(t('manager.branding.notifications.defaultThemeUpdatedError'), 'negative');
        }
      },
    });
  };

  useEffect(
    () => () => {
      themePreviewVar(undefined);
    },
    []
  );

  if (!isAccessToBrandingPageAllowed) {
    return (
      <ErrorPage
        data-testid="access-denied"
        title={t('common.accessDenied.title')}
        subTitle={t('common.accessDenied.subTitle')}
        image={accessDeniedOperatorImage}
        style={{ gridArea: 'section', height: 'unset', background: 'none' }}
      />
    );
  }

  return (
    <section className={classNames(styles.wrapper, playerStyles.wrapper)} data-testid="branding-page">
      <div className={styles.menu}>
        {preview && (
          <>
            <header data-testid="back-to-themes-button" role="button" onClick={() => themePreviewVar(undefined)}>
              <ChevronLeftIcon /> {t('manager.branding.navigation.backToThemes')}
            </header>

            <ThemeSettings />
          </>
        )}

        {!preview && (
          <>
            <header>{t('manager.branding.navigation.channelThemes')}</header>

            {defaultTheme && (
              <InputDescription label={t('manager.branding.navigation.defaultThemeLabel')}>
                <Select
                  data-testid="default-theme-select"
                  optionsIdProperty="key"
                  value={{ key: defaultTheme.id, label: defaultTheme.name }}
                  options={themes?.map((theme) => ({ key: theme.id, label: theme.name }))}
                  onChange={handleChangeDefaultTheme}
                />
              </InputDescription>
            )}

            {themes?.map((theme) => (
              <ThemeListItem key={theme.id} theme={theme} onClick={(id) => themePreviewVar({ themeId: id })} />
            ))}

            <NewThemeModal />
          </>
        )}
      </div>

      <BrandingPreview />
    </section>
  );
}
